import { createTheme } from '@mui/material/styles'

export const tokens = {
  // Business black scale
  businessBlack: '#000000',
  businessBlack60: '#666666',
  businessBlack40: '#999999',
  businessBlack20: '#CCCCCC',
  businessBlack10Solid: '#E5E5E5',
  businessBlack5Solid: '#F2F2F2',

  white: '#FFFFFF',
  white20: 'rgba(255,255,255,0.20)',
  white60: 'rgba(255,255,255,0.60)',

  smokeySilver: '#F2F2F2', // hover surfaces
  smokeySilver5: 'rgba(242,242,242,0.05)',

  // Violet — primary accent (links, focus ring, selected nav/tab)
  versatileViolet: '#5F26E0',
  versatileViolet5: '#F7F4FD',
  versatileViolet10: '#EFE9FC',
  versatileViolet20: '#DFD4F9',

  // Semantic
  rebelliousRed: '#FB6C4A', // error
  rebelliousRed5: 'rgba(251,108,74,0.05)',
  rebelliousRed10: '#FFF0ED',
  rebelliousRed40: '#FEC4B7',
  generousGreen: '#00C04D', // success
  generousGreen10Solid: '#E6F9ED',
  youthfulYellow: '#FBB03B', // warning
  talkativeTurquoise: '#51D7E6', // info

  // Borders
  borderDefault: '#CCCCCC', // the workhorse hairline
  borderSubtle: '#E5E5E5',
  borderStrong: '#666666',

  // The signature paretos gradient — magenta → amber
  paretosGradient: 'linear-gradient(45.03deg, #F500E1 0%, #FBB03B 100%)',

  // Type
  fontFamily: 'AeonikPro, Roboto, Helvetica, Arial, sans-serif',
  fontMono: "'Consolas', 'Courier New', Courier, monospace",

  // Radii / sizing
  radiusSm: '3px',
  radiusMd: '7px',
  radiusLg: '10px',
  radiusPill: '999px',
  topbarHeight: 60,
  shadowDefault: '0 0 10px rgba(0,0,0,0.15)',
} as const

// Viewport-HEIGHT breakpoints. The auth screens are full-height fixed layouts
// (`position: fixed; inset: 0`), so the binding constraint on a laptop is the
// usable viewport height, not its width — a MacBook 13" is ~1440px wide (above
// every width breakpoint) but only ~750–850px tall after browser chrome, which
// is where the header/form/footer start to crowd and overlap. These media
// queries drive the vertical compaction (smaller headings, tighter padding and
// gaps, dropped min-heights) used across the auth layout and pages. Authored as
// raw `@media` keys so they can be spread into any `sx` object.
export const shortScreen = {
  /** ~MacBook 13" and similar — moderate compaction. */
  compact: '@media (max-height: 900px)',
  /** Very short viewports — aggressive compaction. */
  tight: '@media (max-height: 850px)',
} as const

export const brand = {
  primary: tokens.businessBlack,
  primaryHover: tokens.businessBlack60,
  accent: tokens.versatileViolet,
  accentHover: tokens.versatileViolet,
  textDefault: tokens.businessBlack,
  textMuted: tokens.businessBlack60,
  textSubtle: tokens.businessBlack40,
  borderDefault: tokens.borderDefault,
  borderSubtle: tokens.borderSubtle,
  error: tokens.rebelliousRed,
  success: tokens.generousGreen,
  warning: tokens.youthfulYellow,
  info: tokens.talkativeTurquoise,
  paretosGradient: tokens.paretosGradient,
} as const

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tokens.businessBlack,
      dark: tokens.businessBlack,
      light: tokens.businessBlack60,
    },
    secondary: { main: tokens.versatileViolet },
    error: { main: tokens.rebelliousRed },
    success: { main: tokens.generousGreen },
    warning: { main: tokens.youthfulYellow },
    info: { main: tokens.talkativeTurquoise },
    text: { primary: tokens.businessBlack, secondary: tokens.businessBlack60 },
    divider: tokens.borderDefault,
  },
  typography: {
    fontFamily: tokens.fontFamily,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 500,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          background: tokens.white,
          minHeight: '100vh',
        },
      },
    },
  },
})
